import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './uiHomePage/button.tsx';
import MobileNav from './MobileNav.tsx';
import { getAuth, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { User as UserIcon } from 'lucide-react';
import { useChatbot } from './chat/GlobalChatbot.tsx';

interface NavbarProps {
  scrollableElementRef?: React.RefObject<HTMLElement>;
  color?: string; // Hex color string like "#FFFFFF" or "#FFF"
}

const Navbar: React.FC<NavbarProps> = ({ scrollableElementRef, color = '#FFFFFF' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmenuExpanded, setIsSubmenuExpanded] = useState(false);
  const navigate = useNavigate();
  const controls = useAnimation();
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const lastScrollPosition = useRef(0);
  const isInitialMount = useRef(true);
  const scrollListenerRef = useRef<(() => void) | null>(null);

  // Get chatbot state from context
  const { isOpen: isChatOpen, toggleChat } = useChatbot();

  // Helper function to convert hex to RGB
  const hexToRgb = (hex: string) => {
    // Remove the # if present
    hex = hex.replace('#', '');
    
    // Handle shorthand hex (e.g., "#FFF")
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return { r, g, b };
  };

  // Convert the color to RGB
  const rgbColor = hexToRgb(color);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const handleAuthAction = () => {
    if (user) {
      const auth = getAuth();
      signOut(auth)
        .then(() => {
          navigate('/');
          setIsOpen(false);
        })
        .catch(() => {
          setError('Failed to log out');
        });
    } else {
      navigate('/login');
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const scrollableElement = scrollableElementRef?.current || document.getElementById('root');
    if (!scrollableElement) return;

    // Function to update navbar state
    const updateNavbarState = (scrollPosition: number) => {
      const shouldBeScrolled = scrollPosition > 50;
      if (shouldBeScrolled !== isScrolled) {
        setIsScrolled(shouldBeScrolled);
        controls.start({
          width: shouldBeScrolled ? "90%" : "100%",
          maxWidth: shouldBeScrolled ? "1200px" : "100%",
          borderRadius: shouldBeScrolled ? "9999px" : "0px",
          backgroundColor: shouldBeScrolled ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 1)",
          backdropFilter: shouldBeScrolled ? "blur(10px)" : "none",
          boxShadow: shouldBeScrolled ? "0 4px 30px rgba(0, 0, 0, 0.1)" : "none",
          transition: { duration: 0.5, ease: [0.32, 0.72, 0, 1] }
        });
      }
    };

    // Initial state check
    const initialScrollPosition = scrollableElement.scrollTop;
    lastScrollPosition.current = initialScrollPosition;
    
    // Only update the initial state if it's the first mount
    if (isInitialMount.current) {
      updateNavbarState(initialScrollPosition);
      isInitialMount.current = false;
    }

    const handleScroll = () => {
      const currentScrollPosition = scrollableElement.scrollTop;
      lastScrollPosition.current = currentScrollPosition;
      updateNavbarState(currentScrollPosition);
    };

    // Store the scroll listener reference
    scrollListenerRef.current = handleScroll;

    // Handle route changes
    const handleRouteChange = () => {
      // Use the last known scroll position
      updateNavbarState(lastScrollPosition.current);
    };

    scrollableElement.addEventListener('scroll', handleScroll);
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      if (scrollListenerRef.current) {
        scrollableElement.removeEventListener('scroll', scrollListenerRef.current);
      }
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [controls, isScrolled]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleNavigation = (sectionId: string) => {
    setIsOpen(false);
    if (window.location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleHomeClick = () => {
    setIsSubmenuExpanded(true);
    navigate('/');
  };

  const handleSubmenuClick = (sectionId: string) => {
    setIsSubmenuExpanded(false);
    handleNavigation(sectionId);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
      {error && <div className="fixed top-0 right-0 bg-red-500 text-white p-2 m-2 rounded z-50 pointer-events-auto">{error}</div>}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-xl z-40 pointer-events-auto"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      <nav className="hidden md:flex justify-center items-center py-3 pointer-events-auto w-full">
        <motion.div
          initial={false}
          animate={isScrolled ? {
            width: "90%",
            maxWidth: "1200px",
            borderRadius: "9999px",
            backdropFilter: "blur(18px)",
            color: "#222",
            transition: { duration: 0.6, ease: [0.32, 0.72, 0, 1] }
          } : {
            width: "99%",
            maxWidth: "100%",
            borderRadius: "60px",
            background: isLandingPage 
              ? `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, 0.7)` 
              : `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, 1)`,
            backdropFilter: "blur(18px)",
            color: "#222",
            transition: { duration: 0.6, ease: [0.32, 0.72, 0, 1] }
          }}
          className={`flex items-center justify-between px-6 py-3 transition-all duration-700 max-w-6xl w-full mx-auto relative
            ${isScrolled ? 'bg-white/40 border border-white/10 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-4px_rgba(0,0,0,0.1)] rounded-full backdrop-blur-[18px]' : 'bg-transparent'}
          `}
        >
          {/* Logo Section - Far Left */}
          <div className="flex items-center flex-shrink-0 w-1/4">
            <Link to="/" className="flex items-center">
              <img src="/logos/full_band_1.svg" alt="Wedify Logo" className="w-10 h-10 drop-shadow-lg hover:scale-110 transition-transform duration-300 filter brightness-110"/>
              <span className={`ml-3 text-4xl font-extrabold transition-colors duration-700 text-gray-900`}>Wedify</span>
            </Link>
          </div>

          {/* Main Navigation - Centered */}
          <div className="flex items-center justify-center flex-1 w-1/2">
            <div className="flex items-center space-x-8">
              {user ? (
                <>
                  <Link to="/plan" className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-[#FF4E8E]' : 'text-gray-700 hover:text-gray-900'} ${isScrolled ? 'text-base' : 'text-lg'} font-medium`}>Plan</Link>
                  <Link to="/design" className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-[#FF4E8E]' : 'text-gray-700 hover:text-gray-900'} ${isScrolled ? 'text-base' : 'text-lg'} font-medium`}>Design</Link>
                  <Link to="/explore" className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-[#FF4E8E]' : 'text-gray-700 hover:text-gray-900'} ${isScrolled ? 'text-base' : 'text-lg'} font-medium`}>Explore</Link>
                  <Link to="/connect" className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-[#FF4E8E]' : 'text-gray-700 hover:text-gray-900'} ${isScrolled ? 'text-base' : 'text-lg'} font-medium`}>Connect</Link>
                </>
              ) : (
                <>
                  <button onClick={() => handleNavigation('features')} className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-[#FF4E8E]' : 'text-gray-700 hover:text-gray-900'} ${isScrolled ? 'text-base' : 'text-lg'} font-medium`}>Features</button>
                  <button onClick={() => handleNavigation('how-it-works')} className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-[#FF4E8E]' : 'text-gray-700 hover:text-gray-900'} ${isScrolled ? 'text-base' : 'text-lg'} font-medium`}>How It Works</button>
                  <button onClick={() => handleNavigation('planning')} className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-[#FF4E8E]' : 'text-gray-700 hover:text-gray-900'} ${isScrolled ? 'text-base' : 'text-lg'} font-medium`}>Planning</button>
                  <Link to="/vendors" className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-[#FF4E8E]' : 'text-gray-700 hover:text-gray-900'} ${isScrolled ? 'text-base' : 'text-lg'} font-medium`}>Vendors</Link>
                </>
              )}
            </div>
          </div>

          {/* User Controls - Right Aligned */}
          <div className="flex items-center justify-end flex-shrink-0 w-1/4">
            <div className={`flex items-center transition-all duration-700 ${isScrolled ? 'space-x-3' : 'space-x-4'}`}>
              {!user ? (
                <>
                  <Button variant="ghost" onClick={() => navigate('/login', { state: { activeTab: 'signin' } })} className={`${isScrolled ? 'text-gray-900 hover:text-[#FF4E8E] hover:bg-[#FF4E8E]/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} ${isScrolled ? 'text-sm' : 'text-base'}`}>Log In</Button>
                  <Button onClick={() => navigate('/login', { state: { activeTab: 'signup' } })} className={`${isScrolled ? 'bg-[#FF4E8E] text-white hover:bg-[#FF4E8E]/90' : 'bg-gray-900 text-white hover:bg-gray-800'} ${isScrolled ? 'text-sm' : 'text-base'}`}>Sign Up</Button>
                </>
              ) : (
                <>
                  {/* AI Mode Button */}
                  <button
                    onClick={toggleChat}
                    className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all duration-200 flex items-center space-x-2 ${
                      isChatOpen 
                        ? 'bg-[#FF4E8E] text-white hover:bg-[#FF3A7D] shadow-md' 
                        : 'bg-gray-100 text-gray-700 hover:bg-[#FF4E8E]/10 hover:text-[#FF4E8E] border border-gray-200'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${isChatOpen ? 'bg-white' : 'bg-[#FF4E8E]'}`} />
                    <span>AI Mode</span>
                  </button>
                  
                  {/* Profile Icon */}
                  <button
                    onClick={() => navigate('/user-profile')}
                    className={`rounded-full p-2 transition-colors duration-200 hover:bg-[#FF4E8E]/10 focus:outline-none flex items-center justify-center ${isScrolled ? 'text-gray-900' : 'text-gray-700'}`}
                    aria-label="User Profile"
                  >
                    <UserIcon size={20} strokeWidth={2.2} className="drop-shadow-sm" />
                  </button>
                  
                  {/* Log Out Button */}
                  <Button variant="ghost" onClick={handleAuthAction} className={`${isScrolled ? 'text-gray-900 hover:text-[#FF4E8E] hover:bg-[#FF4E8E]/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} ${isScrolled ? 'text-sm' : 'text-base'}`}>Log Out</Button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </nav>

      {/* Mobile Navigation */}
      <MobileNav isOpen={isOpen} setIsOpen={setIsOpen} textColor={isScrolled ? 'text-[#FF4E8E]' : 'text-white'} />
    </div>
  );
};

export default React.memo(Navbar);
